import * as React from 'react';
import { createPortal } from 'react-dom';
import { v4 as uuidv4 } from 'uuid';
import { useModalOrders } from '@/hooks/useModalOrders';

interface Props {
  width?: number;
  children: React.ReactNode;
  onClose?: () => void;
  /**
   * 閉じる直前（backdrop クリック・ESC・close()）に呼ばれる。
   * false を返すとクローズを中止する（アップロード中の誤クローズ防止など）
   */
  onBeforeClose?: () => boolean | Promise<boolean>;
}

export interface ForwardRefHandlers {
  close: () => void;
}

// DESIGN.md §8.7 Modal — surface-base body, radius-xl, shadow-xl, slate-900
// based backdrop. The fade/scale animation now uses the shared keyframes
// declared in globals.css (see `animate-modal-in` etc.) so no per-component
// keyframes blob is needed and we can drop styled-components from this file.
export const ModalLayout = React.forwardRef<ForwardRefHandlers, Props>(
  ({ children, onClose, onBeforeClose, width = 480 }, forwardRef) => {
    const [isDisplay, setIsDisplay] = React.useState(true);
    const [modalId] = React.useState(uuidv4());
    const [, addModal, removeModal, isForeground] = useModalOrders();

    const requestClose = React.useCallback(async () => {
      if (onBeforeClose && !(await onBeforeClose())) {
        return;
      }
      setIsDisplay(false);
    }, [onBeforeClose]);

    const handleClose = () => {
      void requestClose();
    };

    const handleEscape = React.useCallback(
      (event: KeyboardEvent) => {
        if (!isForeground(modalId)) {
          return;
        }
        if (['Escape', 'Esc'].includes(event.key)) {
          event.preventDefault();
          void requestClose();
        }
      },
      [modalId, isForeground, requestClose],
    );

    React.useImperativeHandle(forwardRef, () => {
      return {
        close: () => {
          void requestClose();
        },
      };
    });

    React.useEffect(() => {
      if (!isDisplay && typeof onClose === 'function') {
        setTimeout(() => {
          removeModal(modalId);
          onClose();
        }, 300);
      }
    }, [modalId, isDisplay]);

    React.useEffect(() => {
      document.addEventListener('keydown', handleEscape, false);
      return () => {
        document.removeEventListener('keydown', handleEscape, false);
      };
    }, [handleEscape]);

    React.useEffect(() => {
      if (!modalId) return;
      addModal(modalId);
    }, [modalId]);

    return createPortal(
      <div className="fixed inset-0 z-modal flex justify-center items-center">
        <div
          onClick={handleClose}
          className={[
            'absolute inset-0 bg-[rgba(15,23,42,0.5)] opacity-0',
            isDisplay ? 'animate-backdrop-in' : 'animate-backdrop-out',
          ].join(' ')}
        />
        <div
          style={{ width }}
          className={[
            'relative min-w-[300px] min-h-[160px] max-h-[86vh] max-w-[90vw]',
            'bg-surface-base text-text-primary rounded-xl shadow-xl',
            'opacity-0 origin-center overflow-y-auto overflow-x-hidden',
            isDisplay ? 'animate-modal-in' : 'animate-modal-out',
          ].join(' ')}
        >
          {children}
        </div>
      </div>,
      document.body,
    );
  },
);
ModalLayout.displayName = 'ModalLayout';
